import React, { useContext } from 'react';
import { useQuery } from 'react-query';
import PropTypes from 'prop-types';
import { BoundedContent, Box } from '@vp/swan';
import useSiteData from '../../../hooks/useSiteData';
import { BasicPreloader } from '../../shared/BasicPreloader';
import {
  GET_FEEDBACK_GROUP,
  GET_SHOPPER_ORDER_DETAILS,
} from '../../../constants/reactQueryKeys';
import { getOrderDetails } from '../../../apis/bopApi';
import { DebugModeContext } from '../../contexts/DebugModeContext';
import DebugTools from '../../debugTooling/DebugTools';
import DesignFeedbackErrorPage from './DesignFeedbackErrorPage';
import ErrorHandler from '../../shared/ErrorHandler';
import DesignFeedbackGroupPage from './DesignFeedbackGroupPage';
import { getFeedbackGroupForOrder } from '../../../apis/orderUpdateRequestsApi';
import { DesignFeedbackGroupResponseProvider } from '../../contexts/DesignFeedbackGroupResponseContext';
import { DesignFeedbackRequestGroupProvider } from '../../contexts/DesignFeedbackGroupRequestContext';
import { customerFeedbackGroupStatesFilter } from '../../../constants/customerFeedbackGroupStates';

export const DesignFeedbackGroupApplication = ({
  shopperId,
  orderId,
  groupId,
}) => {
  const { tenantEnvironment, externalAppNavigation: nav } = useSiteData();
  const orderDetailsUrl = nav.orderDetails;
  const { debugEnabled } = useContext(DebugModeContext);

  // Fetch the order
  const {
    isLoading: isOrderFetchLoading,
    isError: isOrderFetchFailed,
    isSuccess: isOrderFetchSuccess,
    data: order,
    error: orderFetchError,
  } = useQuery([GET_SHOPPER_ORDER_DETAILS, orderId, shopperId], async () =>
    getOrderDetails({
      orderId,
      shopperId,
      environment: tenantEnvironment,
    })
  );

  // Fetch design feedback group
  const {
    isLoading: isDesignFeedbackRequestGroupFetchLoading,
    isError: isDesignFeedbackRequestGroupFetchFailed,
    data: designFeedbackRequestGroup,
    error: designFeedbackRequestGroupFetchError,
  } = useQuery(
    [GET_FEEDBACK_GROUP, orderId, groupId, shopperId],
    async () =>
      getFeedbackGroupForOrder({
        orderNumber: orderId,
        shopperId,
        groupId,
        environment: tenantEnvironment,
        states: customerFeedbackGroupStatesFilter,
      }),
    {
      enabled: isOrderFetchSuccess,
    }
  );

  // While the order or design feedback request is being fetched, show a preloader
  if (isOrderFetchLoading || isDesignFeedbackRequestGroupFetchLoading) {
    return <BasicPreloader isCentered my={9} />;
  }

  // If the order fetch fails, show an error page
  if (isOrderFetchFailed) {
    // 4xx not found
    if (
      orderFetchError?.response?.status === 401 ||
      orderFetchError?.response?.status === 404
    ) {
      return <ErrorHandler error={orderFetchError} />;
    }
    // All other errors
    return <DesignFeedbackErrorPage />;
  }

  // If the design feedback request fetch fails...
  if (isDesignFeedbackRequestGroupFetchFailed) {
    // 404 request not found -> redirect to order details
    if (designFeedbackRequestGroupFetchError?.response?.status === 404) {
      window.location.assign(`${orderDetailsUrl}?orderId=${orderId}`);
      return <BasicPreloader isCentered size="super" mt="xl" mb="xl" />;
    }
    // All other errors -> show error page
    return <DesignFeedbackErrorPage />;
  }

  return (
    <>
      {debugEnabled && <DebugTools shopperId={shopperId} order={order} />}
      <DesignFeedbackRequestGroupProvider
        order={order}
        orderId={orderId}
        request={designFeedbackRequestGroup}
      >
        <DesignFeedbackGroupResponseProvider>
          <BoundedContent>
            <Box my={9}>
              <DesignFeedbackGroupPage />
            </Box>
          </BoundedContent>
        </DesignFeedbackGroupResponseProvider>
      </DesignFeedbackRequestGroupProvider>
    </>
  );
};

DesignFeedbackGroupApplication.propTypes = {
  shopperId: PropTypes.string.isRequired,
  orderId: PropTypes.string.isRequired,
  groupId: PropTypes.string.isRequired,
};
